import { Fragment, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { Card } from '../../components/ui/Card'
import { QuickDateRange } from '../../components/ui/DateField'

const lei = (n: number) =>
  n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const nr = (n: number) => n.toLocaleString('ro-RO')
const d = (s: string) => new Date(s).toLocaleDateString('ro-RO')

interface MonthOrdersResp {
  orders: {
    orderId: string
    customerName: string | null
    orderDate: string
    seller: number
    emag: number
  }[]
}

// Comenzile cu voucher dintr-o lună (drill-down).
function MonthOrders({ month }: { month: string }) {
  const { data, isLoading } = useQuery<MonthOrdersResp>({
    queryKey: ['emag-vouchers-month', month],
    queryFn: async () => (await api.get(`/emag/vouchers/by-month/${month}`)).data,
  })
  if (isLoading) return <p className="text-[11px] text-b4l-text-muted">Se încarcă comenzile…</p>
  const orders = data?.orders ?? []
  if (orders.length === 0) return <p className="text-[11px] text-b4l-text-muted">Nicio comandă.</p>
  return (
    <div className="max-h-80 overflow-y-auto">
      <table className="w-full text-[11px]">
        <thead className="sticky top-0 bg-b4l-bg-soft text-left text-b4l-text-muted">
          <tr>
            <th className="py-1 pr-3">Comandă (ID eMAG)</th>
            <th className="py-1 pr-3">Client</th>
            <th className="py-1 pr-3">Data</th>
            <th className="py-1 pr-3 text-right">Voucher seller</th>
            <th className="py-1 pr-3 text-right">Voucher eMAG</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.orderId} className="border-t border-b4l-border/50">
              <td className="py-1 pr-3 font-mono">{o.orderId}</td>
              <td className="py-1 pr-3">{o.customerName ?? '—'}</td>
              <td className="py-1 pr-3">{d(o.orderDate)}</td>
              <td className="py-1 pr-3 text-right text-b4l-green-dark">
                {o.seller ? lei(o.seller) : '—'}
              </td>
              <td className="py-1 pr-3 text-right text-b4l-blue-dark">
                {o.emag ? lei(o.emag) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

interface VerifResp {
  rows: {
    month: string
    orders: number
    seller: number
    sellerGross: number
    emag: number
    dv: number
    deltaEmagDv: number
  }[]
  totals: { seller: number; sellerGross: number; emag: number; dv: number }
}

export function VouchereEmagPage() {
  const thisYear = new Date().getFullYear()
  const [from, setFrom] = useState(`${thisYear}-01-01`)
  const [to, setTo] = useState(`${thisYear}-12-31`)
  const qs = () => {
    const p = new URLSearchParams()
    if (from) p.set('from', from)
    if (to) p.set('to', to)
    const s = p.toString()
    return s ? `?${s}` : ''
  }
  const { data, isLoading } = useQuery<VerifResp>({
    queryKey: ['emag-vouchers-verify', from, to],
    queryFn: async () => (await api.get(`/emag/vouchers/verify${qs()}`)).data,
  })
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const toggle = (m: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(m)) next.delete(m)
      else next.add(m)
      return next
    })

  const TOL = 50 // lei toleranță pt status verde la Δ
  return (
    <div className="space-y-3">
      <h1 className="text-lg font-semibold sm:text-xl">eMag → Vouchere</h1>

      <Card>
        <div className="mb-2 text-xs font-semibold text-b4l-text-muted">
          Perioadă (după data comenzii)
        </div>
        <QuickDateRange
          fromValue={from}
          toValue={to}
          onChange={(f, t) => {
            setFrom(f)
            setTo(t)
          }}
        />
      </Card>

      {data && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <div className="rounded-lg border-l-4 border-l-b4l-green-dark border-b4l-border bg-b4l-green-light/20 p-3">
            <div className="text-xs text-b4l-text-muted">Cost real seller (net)</div>
            <div className="text-lg font-semibold text-b4l-green-dark">
              {lei(data.totals.seller)} lei
            </div>
            <div className="text-[11px] text-b4l-text-muted">
              {lei(data.totals.sellerGross)} lei cu TVA · oferite de tine
            </div>
          </div>
          <div className="rounded-lg border-l-4 border-l-b4l-blue border-b4l-border bg-b4l-blue-light/20 p-3">
            <div className="text-xs text-b4l-text-muted">Voucher eMAG (din comenzi)</div>
            <div className="text-lg font-semibold text-b4l-blue-dark">
              {lei(data.totals.emag)} lei
            </div>
            <div className="text-[11px] text-b4l-text-muted">ar trebui rambursate de eMAG</div>
          </div>
          <div className="rounded-lg border border-b4l-border p-3">
            <div className="text-xs text-b4l-text-muted">Voucher eMAG (raportat de eMAG)</div>
            <div className="text-lg font-semibold">{lei(data.totals.dv)} lei</div>
            <div className="text-[11px] text-b4l-text-muted">
              din desfășurătorul de vouchere (DV)
            </div>
          </div>
          <div className="rounded-lg border border-b4l-border p-3">
            <div className="text-xs text-b4l-text-muted">Δ (comenzi − raportat de eMAG)</div>
            <div
              className={`text-lg font-semibold ${
                Math.abs(data.totals.emag - data.totals.dv) <= TOL
                  ? 'text-b4l-green-dark'
                  : 'text-amber-700'
              }`}
            >
              {lei(data.totals.emag - data.totals.dv)} lei
            </div>
            <div className="text-[11px] text-b4l-text-muted">eMAG raportează corect?</div>
          </div>
        </div>
      )}

      <Card title="Vouchere pe lună">
        {isLoading && <p className="text-sm text-b4l-text-muted">Se încarcă…</p>}
        {data && data.rows.length === 0 && (
          <p className="text-sm text-b4l-text-muted">Niciun voucher în perioadă.</p>
        )}
        {data && data.rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="border-b border-b4l-border text-left text-b4l-text-muted">
                <tr>
                  <th
                    className="cursor-help py-1.5 pr-3 underline decoration-dotted"
                    title="Luna comenzii (după data comenzii, ora României). Click pe rând pentru comenzile cu voucher din lună."
                  >
                    Lună
                  </th>
                  <th
                    className="cursor-help py-1.5 pr-3 text-right underline decoration-dotted"
                    title="Câte comenzi distincte din lună au folosit cel puțin un voucher (seller sau eMAG)."
                  >
                    Comenzi cu voucher
                  </th>
                  <th
                    className="cursor-help py-1.5 pr-3 text-right underline decoration-dotted"
                    title="Voucherele oferite de TINE (seller), valoare netă (fără TVA). E costul tău real — le suporți tu, diminuează încasările."
                  >
                    Voucher seller (cost real)
                  </th>
                  <th
                    className="cursor-help py-1.5 pr-3 text-right underline decoration-dotted"
                    title="Voucherele oferite de eMAG, calculate de noi din comenzi (product_voucher_split, sursă API). Atât ar trebui să-ți ramburseze eMAG."
                  >
                    Voucher eMAG (din comenzi)
                  </th>
                  <th
                    className="cursor-help py-1.5 pr-3 text-right underline decoration-dotted"
                    title="Voucherele eMAG așa cum le raportează eMAG în desfășurătorul de vouchere (DV) (Vouchere). Ce-ți creditează/raportează eMAG efectiv."
                  >
                    Voucher eMAG (raportat de eMAG)
                  </th>
                  <th
                    className="cursor-help py-1.5 pr-3 text-right underline decoration-dotted"
                    title="Diferența: voucher eMAG din comenzi (API) − raportat de eMAG (dv). Aproape de 0 = eMAG raportează corect. Diferențe mici = decalaj lună comandă vs lună raportare."
                  >
                    Δ (comenzi − raportat)
                  </th>
                  <th
                    className="cursor-help py-1.5 underline decoration-dotted"
                    title="ok = Δ sub 50 lei (eMAG raportează corect). diferență = Δ peste 50 lei (de verificat decalajul de raportare)."
                  >
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => {
                  const ok = Math.abs(r.deltaEmagDv) <= TOL
                  return (
                    <Fragment key={r.month}>
                      <tr
                        onClick={() => toggle(r.month)}
                        className="cursor-pointer border-b border-b4l-border last:border-0 hover:bg-b4l-bg-soft"
                      >
                        <td className="py-1.5 pr-3 font-medium">
                          <span className="mr-1 inline-block text-b4l-text-muted">
                            {expanded.has(r.month) ? '▾' : '▸'}
                          </span>
                          {r.month}
                        </td>
                        <td className="py-1.5 pr-3 text-right">{nr(r.orders)}</td>
                        <td className="py-1.5 pr-3 text-right text-b4l-green-dark">
                          {lei(r.seller)}
                        </td>
                        <td className="py-1.5 pr-3 text-right text-b4l-blue-dark">{lei(r.emag)}</td>
                        <td className="py-1.5 pr-3 text-right">{lei(r.dv)}</td>
                        <td
                          className={`py-1.5 pr-3 text-right ${ok ? '' : 'font-semibold text-amber-700'}`}
                        >
                          {lei(r.deltaEmagDv)}
                        </td>
                        <td className="py-1.5">
                          {ok ? (
                            <span className="text-b4l-green-dark">ok</span>
                          ) : (
                            <span className="text-amber-700">diferență</span>
                          )}
                        </td>
                      </tr>
                      {expanded.has(r.month) && (
                        <tr className="border-b border-b4l-border bg-b4l-bg-soft">
                          <td colSpan={7} className="px-3 py-2">
                            <MonthOrders month={r.month} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-b4l-border font-semibold">
                  <td className="py-1.5 pr-3">TOTAL</td>
                  <td className="py-1.5 pr-3"></td>
                  <td className="py-1.5 pr-3 text-right text-b4l-green-dark">
                    {lei(data.totals.seller)}
                  </td>
                  <td className="py-1.5 pr-3 text-right text-b4l-blue-dark">
                    {lei(data.totals.emag)}
                  </td>
                  <td className="py-1.5 pr-3 text-right">{lei(data.totals.dv)}</td>
                  <td className="py-1.5 pr-3 text-right">
                    {lei(data.totals.emag - data.totals.dv)}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        <div className="mt-3 space-y-1 text-[11px] text-b4l-text-muted">
          <p className="font-medium text-b4l-text">
            Explicații coloane (treci cu mouse-ul pe antet):
          </p>
          <p>
            <span className="font-medium">Lună</span> = luna comenzii (după data comenzii, ora RO).
            Click pe rând → comenzile cu voucher din lună.
          </p>
          <p>
            <span className="font-medium">Comenzi cu voucher</span> = câte comenzi distincte din
            lună au folosit cel puțin un voucher (seller sau eMAG).
          </p>
          <p>
            <span className="font-medium text-b4l-green-dark">Voucher seller (cost real)</span> =
            reducerile oferite de tine, valoare netă. E costul tău real — le suporți tu, diminuează
            încasările. (cu TVA = vezi cardul de sus).
          </p>
          <p>
            <span className="font-medium text-b4l-blue-dark">Voucher eMAG (din comenzi)</span> =
            reducerile oferite de eMAG, calculate de noi din comenzi (sursă API). Clientul plătește
            mai puțin, dar eMAG ți le rambursează (neutru pentru tine). Atât ar trebui rambursat.
          </p>
          <p>
            <span className="font-medium">Voucher eMAG (raportat de eMAG)</span> = aceleași vouchere
            eMAG, dar așa cum le raportează eMAG în desfășurătorul de vouchere (DV) (Vouchere).
            Ce-ți creditează eMAG efectiv.
          </p>
          <p>
            <span className="font-medium">Δ (comenzi − raportat)</span> = voucher eMAG din comenzi −
            raportat de eMAG. Aproape de 0 = eMAG raportează/rambursează corect. (Mici diferențe =
            decalaj între luna comenzii și luna în care eMAG raportează în dv.)
          </p>
          <p>
            <span className="font-medium">Status</span> = ok dacă Δ &lt; 50 lei; diferență dacă Δ
            &gt; 50 lei (de verificat decalajul).
          </p>
        </div>
      </Card>
    </div>
  )
}
